<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import SiteFooter from '../SiteFooter.vue';

const props = defineProps({
  // 'agent' → contact your administrator; 'admin'/anything else → platform.
  role: { type: String, default: 'admin' },
  // 'paused' (per-user) | 'suspended' (whole agency) | 'deactivated' (per-agent, #199).
  reason: { type: String, default: 'paused' },
});

const router = useRouter();

const isAgent = computed(() => props.role === 'agent');
const isSuspended = computed(() => props.reason === 'suspended');
const isDeactivated = computed(() => props.reason === 'deactivated');

const heading = computed(() => {
  if (isDeactivated.value) return 'Account deactivated';
  return isSuspended.value ? 'Agency suspended' : 'Account paused';
});

const goToSignIn = () => {
  router.push({ name: 'login' });
};
</script>

<template>
  <div class="cc-paused-page">
    <div class="cc-paused-noise" />

    <header class="cc-paused-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-pz" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-pz">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-pz)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-pz)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-pz" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-pz)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-paused-main">
      <div class="cc-paused-card">
        <div class="cc-lock-icon">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 class="cc-h-display">{{ heading }}</h1>

        <!-- DEACTIVATED (single agent, #199) -->
        <template v-if="isDeactivated">
          <p class="cc-lede">
            Your account has been deactivated by your admin. For further details, please contact your admin.
          </p>
        </template>
        <!-- SUSPENDED (whole agency) -->
        <template v-else-if="isSuspended">
          <p v-if="isAgent" class="cc-lede">
            Your agency's account has been suspended. Please contact your administrator for further details.
          </p>
          <p v-else class="cc-lede">
            Your agency's account has been suspended. Please contact
            <a href="mailto:info@creator-chat.com" class="cc-link-accent">info@creator-chat.com</a>
            to restore access.
          </p>
        </template>
        <!-- PAUSED (single user) -->
        <template v-else>
          <p v-if="isAgent" class="cc-lede">
            This account is locked at the moment. Please contact your administrator for further details.
          </p>
          <p v-else class="cc-lede">
            Your account has been paused. Please contact
            <a href="mailto:info@creator-chat.com" class="cc-link-accent">info@creator-chat.com</a>
            if you need support.
          </p>
        </template>

        <div class="cc-info-panel">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400e" stroke-width="2" stroke-linecap="round" class="cc-info-icon">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <template v-if="isDeactivated">
              While your account is deactivated you can't access this workspace. Your data is safe and untouched — access is restored the moment your admin reactivates you.
            </template>
            <template v-else>
              While {{ isSuspended ? "your agency's account is suspended" : 'your account is paused' }} you can't sign in or access your workspace. Your data is safe and untouched — access is restored the moment {{ isSuspended ? 'the agency' : 'your account' }} is reactivated.
            </template>
          </div>
        </div>

        <button type="button" class="cc-btn-secondary" @click="goToSignIn">
          Back to sign in
        </button>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.cc-paused-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-paused-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-paused-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark { line-height: 21px; white-space: nowrap; }
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider { display: block; margin: 6px 0 5px 0; }
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-paused-main {
  position: relative;
  z-index: 1;
  flex: 1;
  max-width: 520px;
  width: 100%;
  margin: 60px auto 60px;
  padding: 0 24px;
  box-sizing: border-box;
}

.cc-paused-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 40px 36px 32px;
  text-align: center;
}

.cc-lock-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 18px;
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 26px;
  line-height: 1.2;
  margin: 0 0 10px;
}
.cc-lede {
  font-family: 'Inter', sans-serif;
  font-size: 14.5px;
  color: #2a3434;
  line-height: 1.55;
  margin: 0 0 22px;
}
.cc-link-accent { color: #0f766e; font-weight: 600; text-decoration: none; }
.cc-link-accent:hover { text-decoration: underline; }

.cc-info-panel {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 11px;
  padding: 14px 16px;
  text-align: left;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #92400e;
  line-height: 1.55;
}
.cc-info-icon { flex-shrink: 0; margin-top: 1px; }

.cc-btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 22px;
  border-radius: 10px;
  border: 1px solid #cfc5ac;
  background: #ffffff;
  color: #2a3434;
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.cc-btn-secondary:hover { background: #f1ede4; }

@media (max-width: 540px) {
  .cc-paused-header { padding: 18px 20px; }
  .cc-paused-main { margin: 30px auto 40px; }
  .cc-paused-card { padding: 32px 24px; }
  .cc-h-display { font-size: 22px; }
  .cc-tagline { display: none; }
}
</style>
